
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Newspaper, Sparkles, Brain, Loader2, RotateCcw, Check, Info, Lightbulb, PlayCircle } from "lucide-react";
import { useAdaptiveNotes } from '@/hooks/useAdaptiveNotes';
import { toast } from 'sonner';

export default function AdaptiveCurrentAffairs() {
  const [originalContent, setOriginalContent] = useState("");
  const [adaptedContent, setAdaptedContent] = useState("");
  const [includeQuestions, setIncludeQuestions] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { adaptCurrentAffairsContent } = useAdaptiveNotes();

  const handleAdaptContent = async () => {
    if (!originalContent.trim()) {
      toast.error("Please enter some content to adapt");
      return;
    }

    setIsProcessing(true);
    setIsSuccess(false);

    try {
      // Add a small delay to show processing state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const result = await adaptCurrentAffairsContent(originalContent, {
        includeQuestions
      });
      
      if (result) {
        setAdaptedContent(result);
        setIsSuccess(true);
        toast.success("Content adapted successfully");
      } else {
        toast.error("Failed to adapt content");
      }
    } catch (error) {
      console.error("Error adapting content:", error);
      toast.error("An error occurred while adapting content");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setAdaptedContent("");
    setIsSuccess(false);
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Brain className="h-6 w-6 text-primary" />
          <span className="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
            Adaptive Current Affairs
          </span>
        </h2>
        <p className="text-muted-foreground mt-1">
          Personalize current affairs content based on your learning style and needs
        </p>
      </div>
      
      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="bg-indigo-100 p-2 rounded-full">
              <PlayCircle className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-medium text-indigo-900">Try our new adaptive quiz experience!</h3>
              <p className="text-sm text-indigo-700 mt-1">
                Test your knowledge with questions that adapt to your skill level in real time.
              </p>
            </div>
          </div>
          <Link to="/current-affairs/adaptive-quiz">
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <Brain className="mr-2 h-4 w-4" />
              Take Adaptive Quiz
            </Button>
          </Link>
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 mt-4">
        <Card className="border-primary/10 h-full">
          <CardHeader className="bg-gradient-to-r from-primary/5 to-blue-500/5 pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Newspaper className="h-5 w-5 text-primary" /> 
              Original Content
            </CardTitle>
            <CardDescription>
              Paste news or current affairs content here
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <Textarea 
              placeholder="Paste your current affairs content here..." 
              className="min-h-[300px]"
              value={originalContent}
              onChange={(e) => setOriginalContent(e.target.value)}
            />
            
            <div className="bg-muted/20 p-3 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="include-questions" className="flex items-center gap-2 cursor-pointer">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Include practice questions
                </Label>
                <Switch 
                  id="include-questions" 
                  checked={includeQuestions}
                  onCheckedChange={setIncludeQuestions}
                />
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={handleAdaptContent} 
                  disabled={isProcessing || !originalContent.trim()}
                  className="edaptica-button"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Brain className="mr-2 h-4 w-4" />
                      Adapt Content
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-primary/10 h-full ${adaptedContent ? 'ring-1 ring-primary/20' : ''}`}>
          <CardHeader className="bg-gradient-to-r from-violet-50 to-purple-50 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="h-5 w-5 text-purple-500" /> 
                Adapted Content
              </CardTitle>
              {adaptedContent && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleReset}
                  className="h-8 px-2 text-muted-foreground"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
                </Button>
              )}
            </div>
            <CardDescription>
              Personalized learning content
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            {adaptedContent ? (
              <Tabs defaultValue="content">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="content">Content</TabsTrigger>
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                </TabsList>
                <TabsContent value="content" className="mt-4">
                  <Textarea 
                    value={adaptedContent}
                    onChange={(e) => setAdaptedContent(e.target.value)}
                    className="min-h-[300px]"
                  />
                </TabsContent>
                <TabsContent value="preview" className="mt-4">
                  <div className="prose prose-sm max-w-none">
                    <div dangerouslySetInnerHTML={{ 
                      __html: adaptedContent.replace(/\n/g, '<br />') 
                    }} />
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="flex items-center justify-center h-[300px] bg-muted/10 rounded-lg border border-dashed">
                <div className="text-center max-w-xs">
                  <Sparkles className="mx-auto h-10 w-10 text-muted-foreground/50 mb-2" />
                  <h3 className="font-medium text-muted-foreground">
                    Your adapted content will appear here
                  </h3>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Paste content in the left panel and click "Adapt Content"
                  </p>
                </div>
              </div>
            )}
            
            {isSuccess && (
              <div className="mt-4 bg-green-50 border border-green-100 rounded-lg p-3 flex items-start">
                <div className="bg-green-100 p-1 rounded-full mr-3">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-green-800 font-medium">Content successfully adapted</p>
                  <p className="text-xs text-green-700 mt-1">
                    The content has been personalized based on your learning profile and performance history
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Separator className="my-8" />
      
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mt-4">
        <div className="flex items-start">
          <div className="bg-blue-100 p-1 rounded-full mr-3">
            <Info className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-blue-800">How does adaptive learning work?</h3>
            <p className="text-sm text-blue-700 mt-1">
              This feature analyzes your learning patterns, quiz performance, and cognitive profile to personalize content specifically for you. 
              The more you use the platform, the better it becomes at adapting content to your needs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
